/**
 * API Client pour Reminder Policies
 * ✅ SKILL FACTURXPRO: Architecture 3 couches
 * 🔒 Champs alignés avec Prisma schema: name (not label), entityType (not appliesTo)
 */

import { safeFetch, safeJsonParse, getAuthHeaders } from './api';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


// ==================== TYPES ====================

export interface ReminderLevel {
  daysAfter: number;
  emailTemplateId?: string;
  emailTemplate?: {
    subject: string;
    body: string;
  };
}

export interface ReminderPolicy {
  id: string;
  tenantId: string;
  companyId: string;
  name: string; // ✅ Aligned with Prisma schema (not label)
  entityType: string; // ✅ Aligned with Prisma schema (not appliesTo) - "invoice" or "quote"
  levels: ReminderLevel[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReminderPolicyDTO {
  entityType: 'invoice' | 'quote';
  levels: ReminderLevel[];
}

export interface UpdateReminderPolicyDTO {
  levels?: ReminderLevel[];
  isActive?: boolean;
}

export interface ReminderPoliciesResponse {
  success: boolean;
  data: ReminderPolicy[];
  count: number;
}

export interface ReminderPolicyResponse {
  success: boolean;
  data: ReminderPolicy;
  message?: string;
}

export interface DeleteReminderPolicyResponse {
  success: boolean;
  message: string;
}

// ==================== API CLIENT ====================

export const reminderPoliciesAPI = {
  /**
   * GET /api/reminders/policies
   * Récupère toutes les politiques de relance
   */
  async getAll(): Promise<ReminderPoliciesResponse> {
    const response = await safeFetch(`${API_URL}/reminders/policies`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors du chargement des politiques de relance');
    }

    return safeJsonParse(response);
  },

  /**
   * GET /api/reminders/policies/:id
   * Récupère une politique par ID
   */
  async getById(id: string): Promise<ReminderPolicyResponse> {
    const response = await safeFetch(`${API_URL}/reminders/policies/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors du chargement de la politique');
    }

    return safeJsonParse(response);
  },

  /**
   * POST /api/reminders/policies
   * Crée une nouvelle politique de relance
   */
  async create(data: CreateReminderPolicyDTO): Promise<ReminderPolicyResponse> {
    const response = await safeFetch(`${API_URL}/reminders/policies`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la création de la politique');
    }

    return safeJsonParse(response);
  },

  /**
   * PUT /api/reminders/policies/:id
   * Met à jour une politique de relance
   */
  async update(id: string, data: UpdateReminderPolicyDTO): Promise<ReminderPolicyResponse> {
    const response = await safeFetch(`${API_URL}/reminders/policies/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la mise à jour de la politique');
    }

    return safeJsonParse(response);
  },

  /**
   * DELETE /api/reminders/policies/:id
   * Supprime une politique de relance
   */
  async delete(id: string): Promise<DeleteReminderPolicyResponse> {
    const response = await safeFetch(`${API_URL}/reminders/policies/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la suppression de la politique');
    }

    return safeJsonParse(response);
  },

  /**
   * PATCH /api/reminders/policies/:id/toggle
   * Active/Désactive une politique de relance
   */
  async toggleActive(id: string, isActive: boolean): Promise<ReminderPolicyResponse> {
    const response = await safeFetch(`${API_URL}/reminders/policies/${id}/toggle`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    });

    if (!response.ok) {
      throw new Error('Erreur lors du changement de statut de la politique');
    }

    return safeJsonParse(response);
  },
};
